import { SHStack } from "@components/design-systems";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";
import { RootState } from "@redux/store";
import { isNil } from "lodash";
import { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { DynamicNumberCell } from "../dynamic-number-cell";

interface PerformanceNumberCellProps {
  value?: string;
  valueInPercent?: string;
  valueExcPerf?: string;
  valueExcPerfInPercent?: string;
  currencyDecimalPlaces?: number;
  percentageDecimalPlaces?: number;
  forceCurrencyDecimalScale?: boolean;
  forcePercentageDecimalScale?: boolean;
  renderValue?: (value: string) => JSX.Element;
}

export const PerformanceNumberCell = memo(
  ({
    value,
    valueInPercent,
    valueExcPerf,
    valueExcPerfInPercent,
    currencyDecimalPlaces = 2,
    percentageDecimalPlaces = 3,
    forceCurrencyDecimalScale = true,
    forcePercentageDecimalScale = false,
    renderValue,
  }: PerformanceNumberCellProps) => {
    const storedTableViewStyle = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.internal?.feeAnalysis?.displayStyle,
    );
    const storedPerformanceScope = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.internal?.feeAnalysis?.performanceScope,
    );

    const isShowingPercentage = useMemo(
      () =>
        !isNil(valueInPercent) &&
        storedTableViewStyle === FeesDisplayStyle.Percentage,
      [storedTableViewStyle, valueInPercent],
    );

    const isExcludingPerformance = useMemo(
      () => storedPerformanceScope === FeesPerformanceScope.Excluding,
      [storedPerformanceScope],
    );

    const totalValueInPercent = useMemo(() => {
      if (!isExcludingPerformance) return valueInPercent;
      return valueExcPerfInPercent;
    }, [isExcludingPerformance, valueInPercent, valueExcPerfInPercent]);

    const totalValue = useMemo(() => {
      if (!isExcludingPerformance) return value;
      return valueExcPerf;
    }, [isExcludingPerformance, value, valueExcPerf]);

    return (
      <SHStack width={"100%"} alignItems={"center"}>
        {!isShowingPercentage && (
          <DynamicNumberCell
            value={totalValue}
            prefix={"$"}
            renderValue={renderValue}
            forceDecimalScale={forceCurrencyDecimalScale}
            decimalScale={currencyDecimalPlaces}
          />
        )}

        {isShowingPercentage && (
          <DynamicNumberCell
            value={totalValueInPercent}
            suffix={"%"}
            renderValue={renderValue}
            forceDecimalScale={forcePercentageDecimalScale}
            decimalScale={percentageDecimalPlaces}
          />
        )}
      </SHStack>
    );
  },
);
